import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { InlineSpinner } from '../components/LoadingState';
import LoadingState from '../components/LoadingState';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCopy,
  FaPhone,
  FaDownload,
  FaExclamationTriangle,
  FaFileCsv,
  FaHourglassHalf,
  FaPlay,
  FaStop,
  FaTimes,
  FaUserCheck,
  FaUserPlus
} from 'react-icons/fa';
import { getJobDetail, controlGenesysJob, assignusers } from '../api/api';
import { listAssignableUsers } from '../api/users';
import { useParams, useNavigate } from 'react-router-dom';

import { ContactTable } from '../components/ContactTable';
import { callDetailUrl, toCsv } from '../common/csv';
import RefreshButton from '../components/RefreshButton';
import ConfirmationModal from '../components/ConfirmationModal';
import { qaScoreOf } from '../common/assessment';

const JobDetail = ({ setAlert }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [metadata, setMetadata] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [callsNextToken, setCallsNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  // One slot for whichever confirmation is pending — native window.confirm()
  // doesn't fit the app's design language, so this drives the themed
  // ConfirmationModal instead. { title, message, onConfirm }
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers]   = useState([]);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const [isLoadingUsers, setIsLoadingUsers]   = useState(false);
  const [isAssigning, setIsAssigning]         = useState(false);

  // Genesys ingestion panel state
  const [genesysProgress, setGenesysProgress] = useState(null);
  const [genesysSubmittedCount, setGenesysSubmittedCount] = useState(0);
  const [controlling, setControlling] = useState(false);
  const [showSamplingAgents, setShowSamplingAgents] = useState(false);

  // Load job details
  const loadJobDetail = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getJobDetail(jobId);
      setMetadata(data.metadata);
      setStatistics(data.statistics);
      setCalls(data.calls || []);
      setTrackers(data.trackers || []);
      setCallsNextToken(data.callsNextToken || null);
      setHasLoaded(true);
      // Seed progress from REST response (supports stopped-and-returned-later)
      if (data.metadata?.genesysProgress) {
        setGenesysProgress(data.metadata.genesysProgress);
      }
      if (data.metadata?.genesysSubmittedCount !== undefined) {
        setGenesysSubmittedCount(data.metadata.genesysSubmittedCount);
      }
    } catch (err) {
      console.error('Error loading job details:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Load more calls
  const loadMoreCalls = useCallback(async () => {
    if (!callsNextToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await getJobDetail(jobId, { callsNextToken });
      setCalls(prev => [...prev, ...(data.calls || [])]);
      setCallsNextToken(data.callsNextToken || null);
    } catch (err) {
      console.error('Error loading more calls:', err);
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load more calls.' });
    } finally {
      setIsLoadingMore(false);
    }
  }, [jobId, callsNextToken, isLoadingMore, setAlert]);

  // Load job details on mount, and again whenever the jobId param changes —
  // a job→job navigation keeps this component mounted, so without the reset
  // the previous job's data would stay on screen and never refetch.
  React.useEffect(() => {
    setHasLoaded(false);
  }, [jobId]);

  React.useEffect(() => {
    if (!hasLoaded && jobId) {
      loadJobDetail();
    }
  }, [hasLoaded, jobId, loadJobDetail]);

  // Refresh function
  const mutate = useCallback(() => {
    loadJobDetail();
  }, [loadJobDetail]);

  // Stop or resume the Genesys ingestion Step Function
  const handleControl = async (action) => {
    setControlling(true);
    try {
      await controlGenesysJob(jobId, action);
      if (action === 'stop') {
        setGenesysProgress(p => ({ ...(p || {}), type: 'stopped', message: 'Ingestion stopped by user' }));
        setMetadata(m => ({ ...m, genesysStatus: 'STOPPED' }));
      } else if (action === 'resume') {
        setGenesysProgress(p => ({ ...(p || {}), type: 'progress', phase: 'resuming', message: 'Resuming ingestion…' }));
        setMetadata(m => ({ ...m, genesysStatus: 'RUNNING' }));
      }
    } catch (err) {
      console.error('Control error:', err);
      setAlert({ variant: 'error', heading: 'Error', text: `Failed to ${action} ingestion.` });
    } finally {
      setControlling(false);
    }
  };

  const handleAssignClick = async () => {
    setSelectedUsers([]);
    setAssignModalOpen(true);
    setIsLoadingUsers(true);
    try {
      const data = await listAssignableUsers();
      setAvailableUsers(data.users || data.Users || []);
    } catch (err) {
      console.error('Failed to load users', err);
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load assignable users.' });
      setAssignModalOpen(false);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const toggleUser = (user) => {
    // Key selection on username — email can be missing, which would conflate users
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.username === user.username);
      return exists
        ? prev.filter(u => u.username !== user.username)
        : [...prev, { username: user.username, email: user.email, name: user.username }];
    });
  };

  const handleAssignSubmit = async () => {
    setIsAssigning(true);
    try {
      await assignusers({ jobId, agents: selectedUsers });
      setAssignModalOpen(false);
      setAlert({ variant: 'success', heading: 'Job Assigned', text: `Job assigned to ${selectedUsers.length} agent(s).` });
      // Reload so the Assigned To column reflects the change immediately.
      loadJobDetail();
    } catch (err) {
      console.error('Assignment failed', err);
      // The backend 404s when the job has no completed calls yet — that's a
      // "not ready" condition, not a failure, so warn instead of erroring.
      if (err.status === 404) {
        setAssignModalOpen(false);
        setAlert({
          variant: 'warning',
          heading: 'No Calls to Assign Yet',
          text: 'This job has no completed calls yet. Try again once processing has finished.',
        });
      } else {
        setAlert({ variant: 'error', heading: 'Assignment Failed', text: 'Failed to assign the job. Please try again.' });
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <LoadingState variant="stats" label="Loading job details" />
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-red-50">
          <p className="text-red-700">Failed to load job details. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-yellow-50">
          <p className="text-yellow-700">Job not found.</p>
        </div>
      </div>
    );
  }

  const exportResults = (format) => {
    if (callsNextToken) {
      setConfirmDialog({
        title: 'Export Incomplete Data',
        message: 'Are you sure you want to export without loading all calls?',
        onConfirm: () => doExportResults(format),
      });
      return;
    }
    doExportResults(format);
  };

  const doExportResults = (format) => {
    setConfirmDialog(null);
    if (format === 'csv') {
      const headers = ['Timestamp', 'Job Name', 'Agent', 'Campaign', 'Template', 'QA Score', 'Compliance', 'Conduct', 'Duration', 'GUID', 'Conversation ID', 'DNIS', 'Link'];
      const rows = calls.map(item => [
        // Guard: missing/invalid timestamps would make toISOString() throw
        !isNaN(new Date(item.timestamp).getTime()) ? new Date(item.timestamp).toISOString() : '',
        item.jobName || '',
        item.agent || '',
        item.customer || '',
        item.template || '',
        qaScoreOf(item) ?? 'N/A',
        item.Result?.Compliance ? 'Pass' : 'Fail',
        // Absent means nothing was assessed - not a pass.
        item.Result?.Conduct === false ? 'Fail' : item.Result?.Conduct === true ? 'Pass' : 'N/A',
        item.duration || '',
        item.guid || '',
        item.conversationId || '',
        item.dnis || '',
        callDetailUrl(item)
      ]);
      const csv = toCsv(headers, rows);

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(calls, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const TRACKER_STATUS_CONFIG = {
    queued:        { label: 'Queued',        cls: 'bg-gray-100 text-gray-700 border-gray-200',      pulse: true  },
    transcribing:  { label: 'Transcribing',  cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', pulse: true  },
    processing:    { label: 'Processing',    cls: 'bg-amber-50 text-amber-700 border-amber-200',    pulse: true  },
    failed:        { label: 'Failed',        cls: 'bg-red-50 text-red-700 border-red-200',          pulse: false },
    quota_stopped: { label: 'Quota Stopped', cls: 'bg-amber-100 text-amber-800 border-amber-300',   pulse: false },
  };

  const TrackerStatusBadge = ({ status }) => {
    const cfg = TRACKER_STATUS_CONFIG[status] || TRACKER_STATUS_CONFIG.queued;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${cfg.cls}`}>
        {cfg.pulse && (
          <span className="inline-block h-1.5 w-1.5 rounded-full animate-pulse bg-current" />
        )}
        {cfg.label}
      </span>
    );
  };

  const genesysStatus = metadata?.genesysStatus;
  const isLimitReached = genesysStatus === 'QUOTA_LIMIT';
  const isStopped = genesysStatus === 'STOPPED' || genesysProgress?.type === 'stopped';
  const isComplete = genesysStatus === 'COMPLETE' || genesysProgress?.type === 'complete';
  const isRunning = genesysStatus === 'RUNNING' && !isComplete && !isLimitReached;

  return (
    <div className="space-y-6">
      <ConfirmationModal
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm}
      />
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
            {jobId === 'legacy' ? (
              <p className="text-gray-500 text-sm mt-1.5">Legacy Job</p>
            ) : (
              <span className="inline-flex items-center gap-1.5 mt-1.5 pl-2.5 pr-1 py-0.5 rounded-lg bg-gray-100 font-mono text-xs text-gray-600">
                {jobId}
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(jobId);
                    setAlert?.({ variant: 'success', heading: 'Copied', text: 'Job ID copied to clipboard.' });
                  }}
                  className="p-1 rounded-md text-gray-400 hover:text-cloudza-600 hover:bg-gray-100 transition-colors"
                  aria-label="Copy Job ID"
                  title="Copy Job ID"
                >
                  <FaCopy className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={handleAssignClick} className="btn-primary">
            <FaUserCheck />
            Assign Job
          </button>
          <RefreshButton onRefresh={mutate} />
        </div>
      </div>

      {/* Job Metadata */}
      <div className="card">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Job Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Upload Date</p>
            <p className="text-base font-medium text-gray-900 mt-1">
              {formatDate(metadata.uploadDate)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Campaign</p>
            <p className="text-base font-medium text-gray-900 mt-1">{metadata.campaign}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Template</p>
            <p className="text-base font-medium text-gray-900 mt-1">{metadata.template}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Uploaded By</p>
            <p className="text-base font-medium text-gray-900 mt-1">{metadata.uploadedBy}</p>
          </div>
        </div>
      </div>

      {/* PCA quota limit banner — shown when non-Genesys calls were stopped by quota */}
      {metadata.quotaStoppedCount > 0 && !genesysStatus && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Daily transcription limit reached</p>
            <p className="text-sm text-amber-700 mt-1">
              {metadata.quotaStoppedCount} call{metadata.quotaStoppedCount !== 1 ? 's' : ''} could not
              be transcribed because the daily Transcribe quota was exhausted. Contact a SuperAdmin to
              increase the limit and resume these calls via the Transcribe Quota page.
            </p>
          </div>
        </div>
      )}

      {/* Genesys quota limit card — replaces ingestion panel when QUOTA_LIMIT */}
      {isLimitReached && (
        <div className="card border-l-4 border-amber-400">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0 text-xl" />
            <div className="flex-1">
              <h2 className="text-sm font-bold text-gray-900">Daily Transcription Limit Reached</h2>
              <p className="text-sm text-gray-600 mt-1">
                This job was paused because the daily Transcribe quota was exhausted.
                No further recordings will be processed until the limit is increased.
              </p>
              <p className="text-sm font-medium text-amber-700 mt-2">
                Contact a SuperAdmin to increase the daily limit and resume processing
                via the Transcribe Quota page.
              </p>
              {metadata.quotaStoppedCount > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {metadata.quotaStoppedCount} execution{metadata.quotaStoppedCount !== 1 ? 's' : ''} paused by quota
                </p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
              Quota limit reached
            </span>
          </div>
        </div>
      )}

      {/* Genesys Ingestion Panel */}
      {(genesysProgress || genesysStatus) && !isLimitReached && (
        <div className="card border-l-4 border-cloudza-500">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Genesys Ingestion Workflow</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Recordings are submitted in batches of up to 100. Runs independently of QA scoring.
              </p>
            </div>
            <div>
              {isStopped && !isComplete && (
                <button
                  onClick={() => handleControl('resume')}
                  disabled={controlling}
                  className="btn-primary"
                >
                  {controlling ? 'Starting…' : <><FaPlay /> Resume Ingestion</>}
                </button>
              )}
              {isRunning && !isStopped && (
                <button
                  onClick={() => handleControl('stop')}
                  disabled={controlling}
                  className="btn-outline !text-red-600 !border-red-200 hover:!bg-red-50 hover:!text-red-700"
                >
                  {controlling ? 'Stopping…' : <><FaStop /> Stop Ingestion</>}
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isStopped ? 'bg-gray-400' : isComplete ? 'bg-green-500' : 'bg-cloudza-600'
              }`}
              style={{ width: `${genesysProgress?.percentage || 0}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600 mb-4">
            <span>{genesysProgress?.message || 'Waiting for first batch…'}</span>
            <span className="font-medium">{genesysProgress?.percentage || 0}%</span>
          </div>

          {/* Batch stats */}
          {genesysProgress?.totalChunks > 0 && (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Batch</p>
                <p className="font-semibold text-gray-900 tabular-nums mt-0.5">
                  {genesysProgress.currentChunk} / {genesysProgress.totalChunks}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Recordings</p>
                <p className="font-semibold text-gray-900 tabular-nums mt-0.5">
                  {genesysSubmittedCount.toLocaleString()} / ~{(genesysProgress.approxTotal || genesysProgress.totalChunks * 100).toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Per batch</p>
                <p className="font-semibold text-gray-900 tabular-nums mt-0.5">≤ 100</p>
              </div>
            </div>
          )}

          <div className="mt-3">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                isStopped
                  ? 'bg-gray-100 text-gray-700'
                  : isComplete
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-cloudza-50 text-cloudza-700'
              }`}
            >
              {isStopped
                ? 'Ingestion stopped'
                : isComplete
                ? 'Ingestion complete'
                : genesysProgress?.phase?.replace(/_/g, ' ') || 'Running'}
            </span>
          </div>

          {/* Per-agent sampling summary */}
          {metadata?.samplingSummary && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Per-Agent Sampling</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {metadata.samplingSummary.agentsFound} agent{metadata.samplingSummary.agentsFound === 1 ? '' : 's'} found
                    {' · '}{metadata.samplingSummary.callsPerAgent} call{metadata.samplingSummary.callsPerAgent === 1 ? '' : 's'}/agent ({metadata.samplingSummary.scope})
                    {' · '}sampled {metadata.samplingSummary.sampledConversations?.toLocaleString()} of {metadata.samplingSummary.totalConversations?.toLocaleString()} calls
                  </p>
                </div>
                {metadata.samplingSummary.agents?.length > 0 && (
                  <button
                    onClick={() => setShowSamplingAgents(v => !v)}
                    className="inline-flex items-center gap-1.5 text-xs text-cloudza-600 hover:text-cloudza-700 font-medium transition-colors"
                  >
                    {showSamplingAgents ? <><FaChevronUp /> Hide agents</> : <><FaChevronDown /> Show agents</>}
                  </button>
                )}
              </div>

              {showSamplingAgents && metadata.samplingSummary.agents?.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Agent</th>
                        <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Sampled</th>
                        <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadata.samplingSummary.agents.map((a, i) => {
                        const underTarget = a.available < metadata.samplingSummary.callsPerAgent;
                        return (
                          <tr
                            key={a.agentId}
                            className={`border-t border-gray-100 hover:bg-cloudza-50/40 transition-colors ${i % 2 === 1 ? 'bg-gray-50/60' : ''}`}
                          >
                            <td className="px-3 py-1.5 text-gray-800">{a.agentName}</td>
                            <td className="px-3 py-1.5 text-right font-medium text-gray-700">{a.sampled}</td>
                            <td className={`px-3 py-1.5 text-right ${underTarget ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                              <span className="inline-flex items-center gap-1.5">
                                {underTarget && <FaExclamationTriangle className="text-[11px]" />}
                                {a.available}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total Calls</p>
              {genesysStatus ? (
                <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
                  {genesysSubmittedCount.toLocaleString()}
                  {genesysProgress?.approxTotal > 0 && (
                    <span className="text-base font-normal text-gray-500"> / ~{genesysProgress.approxTotal.toLocaleString()}</span>
                  )}
                </p>
              ) : (
                <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{statistics.totalCalls}</p>
              )}
            </div>
            <span className="flex-shrink-0 w-9 h-9 bg-cloudza-50 text-cloudza-600 rounded-xl flex items-center justify-center text-sm"><FaPhone /></span>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Completed</p>
              <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{statistics.completedCount}</p>
            </div>
            <span className="flex-shrink-0 w-9 h-9 bg-coral-50 text-coral-600 rounded-xl flex items-center justify-center text-sm"><FaCheckCircle /></span>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Processing</p>
              <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{statistics.processingCount}</p>
            </div>
            <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm ${statistics.processingCount > 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-400'}`}><FaClock /></span>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Failed</p>
              <p className={`text-2xl font-bold tabular-nums mt-1 ${statistics.failedCount > 0 ? 'text-red-700' : 'text-gray-900'}`}>{statistics.failedCount}</p>
            </div>
            <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm ${statistics.failedCount > 0 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-400'}`}><FaExclamationTriangle /></span>
          </div>
        </div>
        <div className="card">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Completion</p>
          <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{statistics.completionPercentage}%</p>
          <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden mt-2.5">
            <div
              className="h-full bg-cloudza-600 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, parseFloat(statistics.completionPercentage) || 0))}%` }}
            />
          </div>
        </div>
      </div>

      {/* Average Score */}
      {statistics.averageScore !== null && (
        <div className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Average QA Score</p>
              <p className="text-2xl font-bold text-cloudza-600 tabular-nums mt-1">{statistics.averageScore}</p>
            </div>
          </div>
        </div>
      )}

      {/* In-Progress Files */}
      {trackers.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <FaHourglassHalf className="text-amber-500" />
            <h2 className="text-sm font-bold text-gray-900">
              Files In Progress
            </h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
              {trackers.length}
            </span>
          </div>
          {trackers.some(t => t.status === 'quota_stopped') && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-amber-500" />
              <span>
                Some files were stopped because the daily Transcribe quota was reached.
                Visit the <strong>Transcribe Quota</strong> page to increase the limit and resume them.
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {['Status', 'File', 'Agent', 'Campaign', 'Template', 'Queued At'].map(h => (
                    <th key={h} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trackers.map((t, i) => (
                  <tr key={t.filename || i} className="bg-white hover:bg-cloudza-50/40 transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <TrackerStatusBadge status={t.status} />
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700 font-mono max-w-xs truncate" title={t.filename}>
                      {t.filename}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.agent || '—'}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.campaign || '—'}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.template || '—'}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {t.timestamp ? new Date(t.timestamp).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calls Table — ContactTable renders its own card, so no wrapper card
          here; the section title travels in via the header slot. */}
      <div>
        <ContactTable
          data={calls}
          setAlert={setAlert}
          showJobId={false}
          header={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                Calls in this Job
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                  {calls.length}
                </span>
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => exportResults('csv')}
                  className="btn-outline !py-1.5 !px-3.5 !text-xs"
                >
                  <FaFileCsv /> Export CSV
                </button>
                <button
                  onClick={() => exportResults('json')}
                  className="btn-outline !py-1.5 !px-3.5 !text-xs"
                >
                  <FaDownload /> Export JSON
                </button>
              </div>
            </div>
          }
        />

        {/* Load More Calls Button */}
        {callsNextToken && (
          <div className="py-4 text-center">
            <button
              onClick={loadMoreCalls}
              disabled={isLoadingMore}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? (
                <>
                  <InlineSpinner className="mr-2" />
                  Loading...
                </>
              ) : (
                <><FaChevronDown /> Load More Calls</>
              )}
            </button>
          </div>
        )}
      </div>
      {/* Assign Job Modal */}
      {assignModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Assign Job</h2>
            <p className="text-sm text-gray-500 mb-4">
              {jobId === 'legacy' ? 'Legacy Job' : `Job ID: ${jobId}`}
            </p>

            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <InlineSpinner className="!h-8 !w-8" />
                <span className="ml-3 text-gray-600">Loading agents...</span>
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="py-10 px-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center mx-auto mb-3">
                  <FaUserPlus />
                </div>
                <p className="text-sm font-semibold text-gray-800">No assignable agents found</p>
                <p className="text-sm text-gray-400 mt-1">Add agents with the QA role to assign this job.</p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg mb-4">
                {availableUsers.map(user => (
                  <label
                    key={user.email || user.username}
                    className="flex items-center px-4 py-3 hover:bg-cloudza-50/40 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded-lg border-gray-200 text-cloudza-600 mr-3"
                      checked={selectedUsers.some(u => u.username === user.username)}
                      onChange={() => toggleUser(user)}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.username}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3 mt-2">
              <button
                onClick={() => setAssignModalOpen(false)}
                className="btn-outline"
                disabled={isAssigning}
              >
                <FaTimes />
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedUsers.length === 0 || isAssigning || isLoadingUsers}
              >
                {isAssigning ? 'Assigning...' : <><FaUserPlus /> {`Assign (${selectedUsers.length})`}</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JobDetail;
